/**
 * The example formula shown on the landing page, and the lab value or intake answer behind each
 * ingredient. It is the same person as `fixtures/labs/sample-1-full-panel.pdf`.
 *
 * Illustrative, not clinical guidance. The values are plausible for an adult who trains and has a
 * few markers worth acting on. A real formula comes from the formula engine and a physician.
 */
import type { LabelEdition } from "./render/pouch.ts";

/** The same ingredient in a later formula. A null dose means the formula no longer has it. */
interface Revision {
  readonly dose: string | null;
  /** What put it at that dose, in the customer's words. */
  readonly basis: string;
}

export interface Ingredient {
  readonly name: string;
  readonly dose: string;
  /** The formulas signed after Jordan's labs at three months and at six, in that order. */
  readonly next: readonly [Revision, Revision];
  /** The lab value or intake answer that put it in the formula, in the customer's words. */
  readonly basis: string;
  /** True for an add-on that cannot be mixed into the powder and ships beside the pouch. */
  readonly addon: boolean;
  readonly why: string;
}

/** U+2011, so "Omega‑3" and "add‑on" never break across a line on a narrow screen. */
const NB = "‑";

export const ingredients: readonly Ingredient[] = [
  {
    name: "Whey protein isolate",
    dose: "30 g",
    next: [
      { dose: "35 g", basis: "Trains 5 days a week, 80 kg" },
      { dose: "35 g", basis: "Trains 5 days a week, 79 kg" },
    ],
    basis: "Trains 4 days a week, 82 kg",
    addon: false,
    why: "The base of every formula, sized to Jordan's weight and training.",
  },
  {
    name: "Creatine monohydrate",
    dose: "5 g",
    next: [
      { dose: "5 g", basis: "Trains 5 days a week" },
      { dose: "5 g", basis: "Trains 5 days a week" },
    ],
    basis: "Trains 4 days a week",
    addon: false,
    why: "Included because Jordan trains 4 days a week.",
  },
  {
    name: "Vitamin D3",
    dose: "2,000 IU",
    next: [
      { dose: "1,000 IU", basis: "Vitamin D 36 ng/mL" },
      { dose: "1,000 IU", basis: "Vitamin D 41 ng/mL" },
    ],
    basis: "Vitamin D 24 ng/mL",
    addon: false,
    why: "Below 30 ng/mL, the bottom of most labs' range.",
  },
  {
    name: "Magnesium glycinate",
    dose: "200 mg",
    next: [
      { dose: "100 mg", basis: "Red cell magnesium 4.8 mg/dL" },
      { dose: null, basis: "Red cell magnesium 5.2 mg/dL" },
    ],
    basis: "Red cell magnesium 4.1 mg/dL",
    addon: false,
    why: "Just under Jordan's lab range of 4.2 to 6.8 mg/dL.",
  },
  {
    name: "Methylfolate",
    dose: "400 mcg",
    next: [
      { dose: "200 mcg", basis: "Homocysteine 9.6 µmol/L" },
      { dose: "200 mcg", basis: "Homocysteine 8.8 µmol/L" },
    ],
    basis: "Homocysteine 16.2 µmol/L",
    addon: false,
    why: "Homocysteine is above Jordan's lab range.",
  },
  {
    name: "Vitamin B12",
    dose: "500 mcg",
    next: [
      { dose: "500 mcg", basis: "Homocysteine 9.6 µmol/L" },
      { dose: "250 mcg", basis: "Homocysteine 8.8 µmol/L" },
    ],
    basis: "Homocysteine 16.2 µmol/L",
    addon: false,
    why: "Paired with methylfolate for the same marker.",
  },
  {
    name: `Omega${NB}3 softgel`,
    dose: "1 g",
    next: [
      { dose: "2 g", basis: `Omega${NB}3 index 5.8%` },
      { dose: "2 g", basis: `Omega${NB}3 index 7.1%` },
    ],
    basis: `Omega${NB}3 index 4.1%`,
    addon: true,
    why: `Fish oil won't stay fresh in a powder. Offered as an add${NB}on at $5 a month.`,
  },
];

/** What goes in the pouch itself. Add-ons ship beside it and are not printed on its label. */
export const inPouch = ingredients.filter((item) => !item.addon);

export type Reading = "low" | "high" | "in range" | "answer";

export interface Result {
  readonly value: string;
  readonly reading: Reading;
}

/**
 * One thing we look at: a lab marker with Jordan's result, or an answer from the health
 * questions. `adds` names the ingredients it puts in the formula, by `Ingredient.name`. An
 * in-range marker adds nothing, which the landing page shows on purpose.
 */
export interface Finding {
  readonly name: string;
  readonly value: string;
  readonly reading: Reading;
  readonly adds: readonly string[];
  /** The same marker or answer at three months and at six, in that order. */
  readonly next: readonly [Result, Result];
}

/**
 * Markers in the order a lab report lists them, then the answers. `next` is Jordan at three months
 * and at six. At three, vitamin D, magnesium and homocysteine are in range, omega-3 is higher but
 * still low, and Jordan trains a day more and weighs 2 kg less, so three doses come down and
 * omega-3 and protein go up. At six, magnesium has been in range twice and comes out, B12 halves,
 * and the rest holds while omega-3 creeps toward range. The page reports these results and never
 * says the formula caused them, which would be a health claim.
 */
export const findings: readonly Finding[] = [
  {
    name: "Vitamin D",
    value: "24 ng/mL",
    reading: "low",
    adds: ["Vitamin D3"],
    next: [
      { value: "36 ng/mL", reading: "in range" },
      { value: "41 ng/mL", reading: "in range" },
    ],
  },
  {
    name: "Vitamin B12",
    value: "412 pg/mL",
    reading: "in range",
    adds: [],
    next: [
      { value: "640 pg/mL", reading: "in range" },
      { value: "690 pg/mL", reading: "in range" },
    ],
  },
  {
    name: "Red cell magnesium",
    value: "4.1 mg/dL",
    reading: "low",
    adds: ["Magnesium glycinate"],
    next: [
      { value: "4.8 mg/dL", reading: "in range" },
      { value: "5.2 mg/dL", reading: "in range" },
    ],
  },
  {
    name: "Ferritin",
    value: "88 ng/mL",
    reading: "in range",
    adds: [],
    next: [
      { value: "79 ng/mL", reading: "in range" },
      { value: "74 ng/mL", reading: "in range" },
    ],
  },
  {
    name: "Homocysteine",
    value: "16.2 µmol/L",
    reading: "high",
    adds: ["Methylfolate", "Vitamin B12"],
    next: [
      { value: "9.6 µmol/L", reading: "in range" },
      { value: "8.8 µmol/L", reading: "in range" },
    ],
  },
  {
    name: "Zinc",
    value: "78 µg/dL",
    reading: "in range",
    adds: [],
    next: [
      { value: "81 µg/dL", reading: "in range" },
      { value: "80 µg/dL", reading: "in range" },
    ],
  },
  {
    name: `Omega${NB}3 index`,
    value: "4.1%",
    reading: "low",
    adds: [`Omega${NB}3 softgel`],
    next: [
      { value: "5.8%", reading: "low" },
      { value: "7.1%", reading: "low" },
    ],
  },
  {
    name: "Training",
    value: "4 days a week",
    reading: "answer",
    adds: ["Whey protein isolate", "Creatine monohydrate"],
    next: [
      { value: "5 days a week", reading: "answer" },
      { value: "5 days a week", reading: "answer" },
    ],
  },
  {
    name: "Weight",
    value: "82 kg",
    reading: "answer",
    adds: ["Whey protein isolate"],
    next: [
      { value: "80 kg", reading: "answer" },
      { value: "79 kg", reading: "answer" },
    ],
  },
];

/** Jordan's first formula as their pouch prints it, everywhere the site shows the example pouch. */
export const examplePouch: LabelEdition = {
  person: "Jordan S.",
  stamp: "EXAMPLE",
  number: "0412 v1",
  note: "One scoop in water daily. 30 servings.",
  lines: inPouch.map((item) => ({
    name: item.name,
    dose: item.dose,
    basis: item.basis,
    was: null,
  })),
};
